import PropTypes from 'prop-types';
import './PrintTemplate.css';

/**
 * Reusable Print Template Component
 * Dynamically renders form data for printing with Prakruti branding
 * Supports multiple layout types for flexible field rendering
 * 
 * @param {Object} formData - The form data to print
 * @param {Array} sections - Section configuration for organizing data
 * @param {String} formTitle - Title of the form
 * @param {String} logo - Logo URL (optional, defaults to prakruti-logo.png)
 * @param {Object} headerConfig - Custom header configuration
 * @param {String} layout - Default layout type: 'grid', 'horizontal', 'vertical', 'table'
 */
const PrintTemplate = ({
    formData = {},
    sections = [],
    formTitle = 'Medical Assessment Form',
    logo = '/prakruti-logo.png',
    headerConfig = {
        hospitalName: 'Prakruti Ayurvedic Health Center & Hospital',
        subtitle: 'A Project By Prakruti Ayurvedic Health Resort, Satara',
        address: 'Satara, Maharashtra, India',
        phone: '+91 9175242908',
        email: 'feelbetter@prakruti.health',
        website: 'www.prakruti.health',
        logo: '/prakruti-logo.png',
    },
    layout = 'grid', // Default layout
    showSignatures = true,
    showPrintDate = true,
    showBlankPage = false,
}) => {
    /**
     * Format field value based on type
     */
    const formatValue = (value, fieldType) => {
        if (value === null || value === undefined || value === '' || (Array.isArray(value) && value.length === 0)) {
            return '-';
        }

        switch (fieldType) {
            case 'checkbox':
            case 'array':
                if (Array.isArray(value)) {
                    return value.join(', ');
                }
                return value;

            case 'date':
                try {
                    const date = new Date(value);
                    if (isNaN(date.getTime())) return value;
                    return date.toLocaleDateString('en-IN', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric',
                    });
                } catch {
                    return value;
                }

            case 'datetime':
                try {
                    const date = new Date(value);
                    if (isNaN(date.getTime())) return value;
                    return date.toLocaleString('en-IN', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                    });
                } catch {
                    return value;
                }

            case 'boolean':
                return value ? 'Yes' : 'No';

            case 'number':
                return value.toString();

            case 'currency':
                return `₹ ${parseFloat(value).toLocaleString('en-IN')}`;

            case 'painRating':
                return `${value} / 10`;

            default:
                return value;
        }
    };

    /**
     * Get layout class based on field configuration
     */
    const getFieldLayoutClass = (field, sectionLayout) => {
        // Field-specific layout takes precedence
        if (field.layout) {
            return getLayoutClass(field.layout, field.gridClass);
        }
        // Section-specific layout
        if (sectionLayout) {
            return getLayoutClass(sectionLayout, field.gridClass);
        }
        // Default layout
        return getLayoutClass(layout, field.gridClass);
    };

    /**
     * Get CSS class based on layout type
     */
    const getLayoutClass = (layoutType, gridClass) => {
        if (gridClass) return gridClass;

        switch (layoutType) {
            case 'horizontal':
                return 'col-12 print-field-horizontal';
            case 'vertical':
                return 'col-12 print-field-vertical';
            case 'table':
                return 'print-field-table';
            case 'grid-2':
                return 'col-6';
            case 'grid-3':
                return 'col-4';
            case 'grid-4':
                return 'col-3';
            case 'full':
                return 'col-12';
            case 'grid':
            default:
                return 'col-6';
        }
    };

    /**
     * Render field based on configuration and layout
     */
    const renderField = (field, sectionLayout) => {
        let value = formData[field.fieldKey];

        // If options are available, try to get the label instead of value
        if (field.options && value !== null && value !== undefined) {
            if (Array.isArray(value)) {
                value = value.map(val => {
                    const option = field.options.find(opt => opt.value === val);
                    return option ? option.label : val;
                });
            } else {
                const option = field.options.find(opt => opt.value === value);
                if (option) value = option.label;
            }
        }

        const formattedValue = formatValue(value, field.componentType);

        // Skip fields only if explicitly told not to show in print
        if (field.showInPrint === false) {
            return null;
        }

        const layoutClass = getFieldLayoutClass(field, sectionLayout);
        const fieldLayout = field.layout || sectionLayout || layout;

        // Render based on layout type
        if (fieldLayout === 'horizontal') {
            return (
                <div key={field.fieldKey} className={`print-field ${layoutClass}`}>
                    <div className="print-field-horizontal-content">
                        <span className="print-field-label">{field.label}:</span>
                        <span className="print-field-value">{formattedValue}</span>
                    </div>
                </div>
            );
        }

        if (fieldLayout === 'table') {
            return (
                <tr key={field.fieldKey} className="print-field-table-row">
                    <td className="print-field-label">{field.label}</td>
                    <td className="print-field-value">{formattedValue}</td>
                </tr>
            );
        }

        // Default grid/vertical layout
        return (
            <div key={field.fieldKey} className={`print-field ${layoutClass}`}>
                <div className="print-field-label">{field.label}:</div>
                <div className="print-field-value">{formattedValue}</div>
            </div>
        );
    };

    /**
     * Render section based on layout
     */
    const renderSection = (section) => {
        const sectionLayout = section.layout || layout;

        // Table layout for entire section
        if (sectionLayout === 'table') {
            return (
                <div key={section.sectionIndex} className="print-section">
                    <div className="print-section-header">
                        <h3>
                            {section.sectionIndex}. {section.sectionTitle}
                        </h3>
                        {section.description && (
                            <p className="print-section-description">{section.description}</p>
                        )}
                    </div>
                    <div className="print-section-content">
                        <table className="print-table">
                            <tbody>
                                {section.fields.map((field) => renderField(field, sectionLayout))}
                            </tbody>
                        </table>
                    </div>
                </div>
            );
        }

        // Grid/Horizontal/Vertical layout
        return (
            <div key={section.sectionIndex} className="print-section">
                <div className="print-section-header">
                    <h3>
                        {section.sectionIndex}. {section.sectionTitle}
                    </h3>
                    {section.description && (
                        <p className="print-section-description">{section.description}</p>
                    )}
                </div>
                <div className={`print-section-content ${sectionLayout === 'horizontal' ? 'print-section-horizontal' : 'row'}`}>
                    {section.fields.map((field) => renderField(field, sectionLayout))}
                </div>
            </div>
        );
    };

    const HeaderComponent = () => (
        <div className="print-header">
            {logo && (
                <div className="print-logo">
                    <img src={logo} alt="Prakruti Logo" />
                </div>
            )}
            <div className="print-header-content">
                <h1 className="print-hospital-name">{headerConfig.hospitalName}</h1>
                {headerConfig.subtitle && (
                    <p className="print-subtitle">{headerConfig.subtitle}</p>
                )}
                <div className="print-contact-info">
                    {headerConfig.address && (
                        <span className="print-contact-item">
                            <i className="contact-icon">📍</i> {headerConfig.address}
                        </span>
                    )}
                    {headerConfig.phone && (
                        <span className="print-contact-item">
                            <i className="contact-icon">📞</i> {headerConfig.phone}
                        </span>
                    )}
                    {headerConfig.email && (
                        <span className="print-contact-item">
                            <i className="contact-icon">✉️</i> {headerConfig.email}
                        </span>
                    )}
                    {headerConfig.website && (
                        <span className="print-contact-item">
                            <i className="contact-icon">🌐</i> {headerConfig.website}
                        </span>
                    )}
                </div>
            </div>
        </div>
    );

    return (
        <div className="print-template" id="printable-area">
            <div id="form-only-area">
                {/* Enhanced Header */}
                <HeaderComponent />

                <div className="print-divider"></div>

                {/* Form Title */}
                <div className="print-form-title">
                    <h2>{formTitle}</h2>
                    {showPrintDate && (
                        <p className="print-date">
                            Printed on: {new Date().toLocaleDateString('en-IN', {
                                day: '2-digit',
                                month: 'long',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                            })}
                        </p>
                    )}
                </div>

                {/* Form Sections */}
                <div className="print-body">
                    {sections.map((section) => renderSection(section))}
                </div>

                {/* Footer with Signatures */}
                {showSignatures && (
                    <div className="print-footer">
                        <div className="print-footer-content">
                            <div className="print-signature-section">
                                <div className="print-signature-box">
                                    <div className="print-signature-line"></div>
                                    <p>Doctor&apos;s Signature</p>
                                </div>
                                <div className="print-signature-box">
                                    <div className="print-signature-line"></div>
                                    <p>Patient&apos;s Signature</p>
                                </div>
                                <div className="print-signature-box">
                                    <div className="print-signature-line"></div>
                                    <p>Date & Time</p>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Footer Branding */}
                <div className="print-footer-branding">
                    <p>This is a computer-generated document from Prakruti Ayurvedic Health Center & Hospital</p>
                </div>
            </div>

            {/* Optional Blank Page for Notes */}
            {showBlankPage && (
                <div className="print-blank-page" id="blank-notes-area">
                    <HeaderComponent />
                    <div className="print-divider"></div>
                    <div className="print-form-title">
                        <h2>Doctor&apos;s Notes / Observations</h2>
                    </div>
                    <div className="print-notes-area">
                        {/* Empty space for manual notes */}
                        {[...Array(15)].map((_, i) => (
                            <div key={i} className="print-notes-line"></div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

PrintTemplate.propTypes = {
    formData: PropTypes.object.isRequired,
    sections: PropTypes.arrayOf(
        PropTypes.shape({
            sectionIndex: PropTypes.number.isRequired,
            sectionTitle: PropTypes.string.isRequired,
            description: PropTypes.string,
            layout: PropTypes.oneOf(['grid', 'horizontal', 'vertical', 'table', 'grid-2', 'grid-3', 'grid-4', 'full']),
            fields: PropTypes.arrayOf(
                PropTypes.shape({
                    fieldKey: PropTypes.string.isRequired,
                    label: PropTypes.string.isRequired,
                    componentType: PropTypes.string,
                    gridClass: PropTypes.string,
                    layout: PropTypes.oneOf(['grid', 'horizontal', 'vertical', 'table', 'grid-2', 'grid-3', 'grid-4', 'full']),
                    required: PropTypes.bool,
                    showInPrint: PropTypes.bool,
                })
            ).isRequired,
        })
    ).isRequired,
    formTitle: PropTypes.string,
    logo: PropTypes.string,
    headerConfig: PropTypes.shape({
        hospitalName: PropTypes.string,
        subtitle: PropTypes.string,
        address: PropTypes.string,
        phone: PropTypes.string,
        email: PropTypes.string,
        website: PropTypes.string,
    }),
    layout: PropTypes.oneOf(['grid', 'horizontal', 'vertical', 'table', 'grid-2', 'grid-3', 'grid-4', 'full']),
    showSignatures: PropTypes.bool,
    showPrintDate: PropTypes.bool,
    showBlankPage: PropTypes.bool,
};

export default PrintTemplate;
